import type { InputMap } from '../input/InputMap';
import type { CharacterMotor } from '../physics/CharacterMotor';
import {
  ACCEL,
  BRAKE,
  COYOTE_S,
  GRAVITY_Y,
  JUMP_SPEED,
  LOOK_SENS,
  PITCH_MAX,
  PITCH_MIN,
  SNEAK_SPEED,
  SPRINT_SPEED,
  WALK_SPEED,
} from './Speeds';
import { Swim, type SwimStamina } from './Swim';

export class Locomotion {
  yaw = 0;
  pitch = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  grounded = false;
  sneak = false;
  jumped = false;
  swimming = false;
  readonly swim = new Swim();
  private coyote = 0;

  get speed(): number {
    return Math.hypot(this.vx, this.vz);
  }

  applyLook(dx: number, dy: number): void {
    this.yaw -= dx * LOOK_SENS;
    this.pitch = clamp(this.pitch - dy * LOOK_SENS, PITCH_MIN, PITCH_MAX);
  }

  integrate(dt: number, input: InputMap, motor: CharacterMotor, stamina?: SwimStamina | null): void {
    const water = this.swim.sample(motor.translation());
    this.swimming = water !== null;
    if (this.swimming && water) {
      this.sneak = false;
      this.jumped = false;
      this.coyote = 0;
      this.swim.drive(dt, input, this, motor, water, stamina);
      return;
    }
    this.swim.tickStamina(dt, false, stamina);
    if (this.swim.active) {
      this.swim.leave();
    }

    this.sneak = input.sneak;
    motor.setSneak(this.sneak);

    const { wishX, wishZ } = wishHorizontal(input, this.yaw, this.sneak);
    const next = approach2(this.vx, this.vz, wishX, wishZ, ACCEL * dt, BRAKE * dt);
    this.vx = next.x;
    this.vz = next.z;

    this.applyGravity(dt);
    if (this.grounded) {
      this.coyote = COYOTE_S;
    } else {
      this.coyote = Math.max(0, this.coyote - dt);
    }
    if (this.coyote > 0 && input.consumeJump()) {
      this.tryJump();
    }
    motor.move(this.vx * dt, this.vy * dt, this.vz * dt, this.vy);
    this.grounded = motor.grounded;
    if (this.grounded && this.vy < 0) {
      this.vy = 0;
    }
  }

  integrateDriven(dt: number, motor: CharacterMotor, vx: number, vz: number): void {
    this.vx = vx;
    this.vz = vz;
    this.applyGravity(dt);
    motor.move(this.vx * dt, this.vy * dt, this.vz * dt, this.vy);
    this.grounded = motor.grounded;
    if (this.grounded && this.vy < 0) {
      this.vy = 0;
    }
  }

  tryJump(): void {
    if (this.coyote <= 0 && !this.grounded) {
      return;
    }
    this.vy = JUMP_SPEED;
    this.grounded = false;
    this.coyote = 0;
    this.jumped = true;
  }

  /** Zero horizontal wish / velocity. Gravity still applies via integrateDriven. */
  halt(): void {
    this.vx = 0;
    this.vz = 0;
    this.jumped = false;
  }

  private applyGravity(dt: number): void {
    this.vy += GRAVITY_Y * dt;
    if (this.grounded && this.vy < 0) {
      this.vy = 0;
    }
  }
}

function wishHorizontal(input: InputMap, yaw: number, sneak: boolean): { wishX: number; wishZ: number } {
  let ix = input.moveX;
  let iz = input.moveZ;
  const mag = Math.hypot(ix, iz);
  if (mag > 1) {
    ix /= mag;
    iz /= mag;
  }
  if (mag === 0) {
    return { wishX: 0, wishZ: 0 };
  }

  const gait = sneak ? SNEAK_SPEED : input.sprint ? SPRINT_SPEED : WALK_SPEED;
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  return {
    wishX: (ix * cos + iz * -sin) * gait,
    wishZ: (ix * -sin + iz * -cos) * gait,
  };
}

function approach2(
  x: number,
  z: number,
  tx: number,
  tz: number,
  accelRate: number,
  brakeRate: number,
): { x: number; z: number } {
  const cur = Math.hypot(x, z);
  const wish = Math.hypot(tx, tz);
  const rate = wish >= cur ? accelRate : brakeRate;
  const dx = tx - x;
  const dz = tz - z;
  const gap = Math.hypot(dx, dz);
  if (gap <= rate || gap < 1e-8) {
    return { x: tx, z: tz };
  }
  return { x: x + (dx / gap) * rate, z: z + (dz / gap) * rate };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
